import { LandingPage, Deployment, PageTemplate, FormLead } from '../types/pageBuilder';
import api from './api';

// Reuses the shared axios instance from ./api so the global request interceptor
// runs — that's the one that attaches Authorization, Accept-Language, and
// (critically) the X-Project-ID header. Without it the backend's projectScope
// middleware ran without `req.project` and the listing returned every LP of
// the user across all their projects.

// Response types
interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  pagination?: {
    total: number;
    page: number;
    totalPages: number;
    hasMore: boolean;
  };
}

interface PaginatedResponse<T> extends ApiResponse<T[]> {
  pagination: {
    total: number;
    page: number;
    totalPages: number;
    hasMore: boolean;
  };
}

// Landing Page CRUD
export const getLandingPages = async (params?: {
  page?: number;
  limit?: number;
  status?: string;
  search?: string;
}): Promise<PaginatedResponse<LandingPage>> => {
  const response = await api.get('/landing-pages', { params });
  return response.data;
};

export const getLandingPage = async (id: string): Promise<ApiResponse<LandingPage>> => {
  const response = await api.get(`/landing-pages/${id}`);
  return response.data;
};

export const createLandingPage = async (data: Partial<LandingPage>): Promise<ApiResponse<LandingPage>> => {
  const response = await api.post('/landing-pages', data);
  return response.data;
};

export const updateLandingPage = async (id: string, data: Partial<LandingPage>): Promise<ApiResponse<LandingPage>> => {
  const response = await api.put(`/landing-pages/${id}`, data);
  return response.data;
};

export const deleteLandingPage = async (id: string): Promise<ApiResponse<void>> => {
  const response = await api.delete(`/landing-pages/${id}`);
  return response.data;
};

// Deployment
export const deployLandingPage = async (id: string): Promise<ApiResponse<{ url: string; deploymentId: string }>> => {
  const response = await api.post(`/landing-pages/${id}/deploy`);
  return response.data;
};

export const getDeployments = async (id: string, params?: {
  page?: number;
  limit?: number;
}): Promise<PaginatedResponse<Deployment>> => {
  const response = await api.get(`/landing-pages/${id}/deployments`, { params });
  return response.data;
};

// Leads
export const getLeads = async (id: string, params?: {
  page?: number;
  limit?: number;
  startDate?: string;
  endDate?: string;
}): Promise<PaginatedResponse<FormLead>> => {
  const response = await api.get(`/landing-pages/${id}/leads`, { params });
  return response.data;
};

// Templates
export const getTemplates = async (): Promise<ApiResponse<PageTemplate[]>> => {
  const response = await api.get('/landing-pages/templates');
  return response.data;
};

// Helper to save page with GrapesJS data
export const saveLandingPageContent = async (
  id: string,
  data: {
    gjsHtml: string;
    gjsCss: string;
    gjsComponents: string;
    gjsStyles: string;
  }
): Promise<ApiResponse<LandingPage>> => {
  const response = await api.put(`/landing-pages/${id}`, {
    gjs_html: data.gjsHtml,
    gjs_css: data.gjsCss,
    gjs_components: data.gjsComponents,
    gjs_styles: data.gjsStyles
  });
  return response.data;
};

// Create and return a new page
export const createAndGetLandingPage = async (data: {
  name: string;
  slug?: string;
  templateId?: string;
}): Promise<LandingPage> => {
  const response = await createLandingPage(data);
  if (!response.success || !response.data) {
    throw new Error(response.message || 'Failed to create landing page');
  }
  return response.data;
};

// Domain Management
export interface VercelDomain {
  name: string;
  verified: boolean;
  configured?: boolean;
  verification?: Array<{
    type: string;
    domain: string;
    value: string;
  }>;
}

export const getDomains = async (id: string): Promise<ApiResponse<{ domains: VercelDomain[] }>> => {
  const response = await api.get(`/landing-pages/${id}/domains`);
  return response.data;
};

export const addDomain = async (id: string, domain: string): Promise<ApiResponse<VercelDomain>> => {
  const response = await api.post(`/landing-pages/${id}/domains`, { domain });
  return response.data;
};

export const removeDomain = async (id: string, domain: string): Promise<ApiResponse<void>> => {
  const response = await api.delete(`/landing-pages/${id}/domains/${domain}`);
  return response.data;
};

// Hub — domínio único servindo todas as LPs publicadas como /slug
export interface LandingPageHub {
  exists: boolean;
  projectId?: string;
  domains: VercelDomain[];
  lastDeployedAt?: string;
  lastError?: string | null;
}

export const getHub = async (): Promise<ApiResponse<LandingPageHub>> => {
  const response = await api.get('/landing-pages/hub');
  return response.data;
};

export const addHubDomain = async (domain: string): Promise<ApiResponse<{ domain: string }>> => {
  const response = await api.post('/landing-pages/hub/domains', { domain });
  return response.data;
};

export const removeHubDomain = async (domain: string): Promise<ApiResponse<void>> => {
  const response = await api.delete(`/landing-pages/hub/domains/${domain}`);
  return response.data;
};

export const syncHub = async (): Promise<ApiResponse<void>> => {
  const response = await api.post('/landing-pages/hub/sync');
  return response.data;
};

// Import landing page from HTML file or pasted code
export const importHtmlLandingPage = async (data: { file?: File; html?: string; name: string }): Promise<ApiResponse<LandingPage>> => {
  if (data.file) {
    const formData = new FormData();
    formData.append('file', data.file);
    formData.append('name', data.name);
    const response = await api.post('/landing-pages/import-html', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  } else {
    const response = await api.post('/landing-pages/import-html', { html: data.html, name: data.name });
    return response.data;
  }
};

export default {
  getLandingPages,
  getLandingPage,
  createLandingPage,
  updateLandingPage,
  deleteLandingPage,
  deployLandingPage,
  getDeployments,
  getLeads,
  getTemplates,
  saveLandingPageContent,
  createAndGetLandingPage,
  importHtmlLandingPage,
  getDomains,
  addDomain,
  removeDomain,
  getHub,
  addHubDomain,
  removeHubDomain,
  syncHub
};
